import React, { useRef } from 'react';
import classnames from 'classnames';
import { breakpoints } from '../../../foundations';
import { AspectRatioType } from '../../../utils';
import { MultiDynamicMediaImageConfigs } from '../../dynamic-media-picture';
import { CampaignCardType, CampaignCard } from '../campaign-card';
import { imageConfigMultiMobile, imageConfigSingleCard } from './image-configs';
import styles from './campaign-card-list.module.scss';
import { Heading } from '../../heading';
import { useScrollTop } from '../use-scroll-top';

export type CampaignCardListType = {
  headline?: string;
  items: Omit<CampaignCardType, 'imageConfigs'>[];
};

const getRatioDesktop = (index: number): AspectRatioType => {
  switch (index) {
    case 1:
      return '3-4';
    case 2:
      return '1-1';
    case 3:
      return '3-4';
    case 4:
      return '16-9';
  }

  return '16-9';
};

const chooseImageConfigs = (isSingleCard: boolean, index: number): MultiDynamicMediaImageConfigs => {
  if (isSingleCard) {
    return imageConfigSingleCard;
  }

  return [
    ...imageConfigMultiMobile,
    {
      aspectRatio: getRatioDesktop(index),
      sizes: '33vw',
      media: `(min-width: ${breakpoints.l / 16}em)`
    }
  ];
};

export const CampaignCardList: React.FC<CampaignCardListType & { isOpen: boolean }> = ({ items, headline, isOpen }) => {
  const isSingleCard = items.length === 1;
  const ref = useRef<HTMLDivElement>(null);

  const classesByIndex = ['ratio-16-9', 'ratio-3-4', 'ratio-1-1', 'ratio-3-4', 'ratio-16-9'];
  const firstItem = items[0];

  useScrollTop(ref, isOpen);

  return (
    <div className={styles.campaignCardContainer} ref={ref}>
      {headline && (
        <Heading tag="h2" variant="headline-functional-400" className={styles.headline}>
          {headline}
        </Heading>
      )}
      {isSingleCard ? (
        <CampaignCard
          imageConfigs={chooseImageConfigs(isSingleCard, 0)}
          imageAltText={firstItem.imageAltText}
          imageUrl={firstItem.imageUrl}
          link={firstItem.link}
          copyText={firstItem.copyText}
          className={styles['single-card']}
        />
      ) : (
        <ul className={styles.list}>
          {items.map((item, i) => (
            <li key={item.imageUrl + i} className={classnames(styles.item, styles[classesByIndex[i]])}>
              <CampaignCard
                imageConfigs={chooseImageConfigs(isSingleCard, i)}
                imageAltText={item.imageAltText}
                imageUrl={item.imageUrl}
                link={item.link}
                copyText={item.copyText}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
